import React, { useState, useEffect, useCallback } from 'react';
import { IonButton, IonToast } from '@ionic/react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AuthService from '../../services/auth';
import ProjectsService from '../../services/projects';
import Marketplace from '../../services/marketplace';
import ProjectsList from './ProjectsList';
import ProjectModal from '../../components/organisms/Modals/ProjectModal/ProjectModal';
import ProjectPickerForDeployModal from '../../components/organisms/Modals/ProjectPickerForDeployModal';
import PageLayout from '../../components/layouts/PageLayout';
import PageHeader from '../../components/atoms/PageHeader/PageHeader';
import './ProjectsPage.css';

/**
 * ProjectsPage — projects list + create / edit modal.
 *
 * Deploy-from-marketplace handoff:
 *   /projects?deployTemplate=<slug> means the user arrived from the public
 *   marketplace clicking Deploy. We resolve the template via
 *   Marketplace.getWorkflow, then either:
 *     - open the project picker (1+ projects) so they can choose where to
 *       deploy, OR
 *     - open the create-project modal in "create & deploy" mode (0 projects)
 *       so a brand-new user can do both in a single form.
 *
 *   On modal cancellation we clear the URL param so refreshing doesn't
 *   re-open the flow with stale intent.
 */
const ProjectsPage = ({ pageRef }) => {
  const [, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', color: '' });

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const deployTemplateSlug = searchParams.get('deployTemplate');

  // Hydrated template metadata (name, icon) for the picker / create-and-
  // deploy modals. Null until the GET resolves.
  const [deployTemplate, setDeployTemplate] = useState(null);
  // Picker is the right surface when there's 1+ existing projects;
  // ProjectModal (in deploy mode) covers the 0-projects case.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployingProjectId, setDeployingProjectId] = useState(null);

  // Show toast notification
  const showToast = (message, color) => {
    setToast({ show: true, message, color });
  };

  // Fetch all projects
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ProjectsService.getProjects();
      setProjects(response.projects || []);
      return response.projects || [];
    } catch (error) {
      console.error('Error fetching projects:', error);
      showToast('Failed to load projects', 'danger');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Get user info + initial fetch
  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      const currentUser = AuthService.getCurrentUser();
      setUser(currentUser);
      fetchProjects();
    }
  }, [fetchProjects]);

  // ── deployTemplate handshake ─────────────────────────────────────────
  // 1) Resolve the template metadata once we know the slug.
  useEffect(() => {
    if (!deployTemplateSlug) {
      setDeployTemplate(null);
      return;
    }
    let cancelled = false;
    Marketplace.getWorkflow(deployTemplateSlug)
      .then((tpl) => {
        if (!cancelled) setDeployTemplate(tpl);
      })
      .catch((err) => {
        console.error('Failed to resolve template for deploy handoff:', err);
        if (!cancelled) {
          showToast(`Couldn't find template "${deployTemplateSlug}"`, 'danger');
          // Strip the bad param so we don't loop on refresh.
          const next = new URLSearchParams(searchParams);
          next.delete('deployTemplate');
          setSearchParams(next, { replace: true });
        }
      });
    return () => { cancelled = true; };
  }, [deployTemplateSlug, searchParams, setSearchParams]);

  // 2) Once the template AND the project list are both ready, open the
  // right surface. Don't re-open if the user already dismissed once
  // (modalOpen / pickerOpen guards that).
  useEffect(() => {
    if (!deployTemplate || loading) return;
    if (modalOpen || pickerOpen) return;
    if (projects.length === 0) {
      // 0 projects → fold project creation + deploy into one form.
      setEditingProject(null);
      setModalOpen(true);
    } else {
      setPickerOpen(true);
    }
  }, [deployTemplate, loading, projects.length, modalOpen, pickerOpen]);

  // Clear ?deployTemplate= from the URL when the user opts out of the
  // flow. Keeps the address bar honest and prevents the auto-open
  // useEffect from re-firing after a manual close.
  const clearDeployIntent = useCallback(() => {
    if (!deployTemplateSlug) return;
    const next = new URLSearchParams(searchParams);
    next.delete('deployTemplate');
    setSearchParams(next, { replace: true });
    setDeployTemplate(null);
  }, [deployTemplateSlug, searchParams, setSearchParams]);

  // ── modal lifecycle ──────────────────────────────────────────────────
  // Open modal for creating new project
  const handleAddProject = () => {
    setEditingProject(null);
    setModalOpen(true);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingProject(null);
    // Closing the create-and-deploy modal counts as opting out of the
    // deploy intent — without this the auto-open useEffect would re-open
    // the modal on the next render.
    if (deployTemplate && !editingProject) {
      clearDeployIntent();
    }
  };

  // ── deploy primitive (shared by picker + create-and-deploy) ─────────
  const deployToProject = useCallback(async (projectId) => {
    if (!deployTemplate?.slug || !projectId) return;
    setDeploying(true);
    setDeployingProjectId(projectId);
    try {
      const res = await Marketplace.deployWorkflow(deployTemplate.slug, projectId, {});
      const deployedAs = res?.workflowType || deployTemplate.slug;
      if (res?.alreadyInstalled) {
        showToast(`${deployTemplate.name || deployTemplate.slug} is already deployed there`, 'warning');
      } else {
        showToast(`Deployed ${deployTemplate.name || deployTemplate.slug}`, 'success');
      }
      // Clear UI state + URL intent before navigating.
      setPickerOpen(false);
      setModalOpen(false);
      clearDeployIntent();
      navigate(`/projects/${projectId}/pipelines/${encodeURIComponent(deployedAs)}`);
    } catch (err) {
      console.error('Deploy failed:', err);
      showToast(`Deploy failed: ${err?.message || 'unknown error'}`, 'danger');
    } finally {
      setDeploying(false);
      setDeployingProjectId(null);
    }
  }, [deployTemplate, navigate, clearDeployIntent]);

  // Handle modal submission (create or update)
  const handleModalSubmit = async (data) => {
    setSubmitting(true);
    try {
      if (editingProject) {
        await ProjectsService.updateProject(editingProject.projectId, data);
        showToast('Project updated successfully', 'success');
        setModalOpen(false);
        setEditingProject(null);
        fetchProjects();
      } else {
        // Create new project. In deploy mode we chain a deploy + navigate;
        // otherwise the original behaviour (copy token, refresh list).
        const response = await ProjectsService.createProject(data);
        const newProject = response?.project;
        const apiToken = response?.apiToken;

        if (deployTemplate && newProject?.projectId) {
          // Refresh the list in the background so when the user comes
          // back to /projects they see the new row, but don't await it
          // (the deploy + navigate is the hot path).
          fetchProjects();
          setModalOpen(false);
          await deployToProject(newProject.projectId);
          // deployToProject handles toast + navigate; nothing more to do.
        } else {
          if (apiToken) {
            navigator.clipboard.writeText(apiToken).catch(() => {});
          }
          showToast('Project created successfully', 'success');
          setModalOpen(false);
          fetchProjects();
        }
      }
    } catch (error) {
      console.error('Error saving project:', error);
      showToast('Failed to save project', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete button click. Optimistic local removal so the row
  // disappears the moment the server confirms — without the full-page
  // refetch flicker (setLoading(true) → empty list → setLoading(false)
  // → list back). On server error, restore the project to the list and
  // toast the failure so the user can retry.
  const handleDelete = async (project) => {
    if (!window.confirm(`Are you sure you want to delete "${project.name}"?`)) {
      return;
    }

    const prevProjects = projects;
    try {
      await ProjectsService.deleteProject(project.projectId);
      setProjects((cur) => cur.filter((p) => p.projectId !== project.projectId));
      showToast('Project deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting project:', error);
      setProjects(prevProjects);
      // Surface the backend's 409 reason (handlers/projects.js sends
      // `error: "Cannot delete project — it still has 2 pipelines..."`).
      // The api client throws an Error whose `.body` has the parsed JSON
      // and whose `.message` is the error string. Prefer the structured
      // body when available so the toast carries the actionable detail.
      const msg = error?.body?.error || error?.message || 'Failed to delete project';
      showToast(msg, 'danger');
    }
  };

  // Handle copy API key button click
  const handleCopyApiKey = async (project) => {
    try {
      if (project.apiToken) {
        await navigator.clipboard.writeText(project.apiToken);
        showToast('API key copied to clipboard', 'success');
      } else {
        showToast('No API key found for this project', 'warning');
      }
    } catch (error) {
      console.error('Error copying API key:', error);
      showToast('Failed to copy API key', 'danger');
    }
  };

  return (
    <>
      <Helmet>
        <title>Projects - Zibby</title>
        <meta name="description" content="Manage your Zibby projects and automation workflows." />
      </Helmet>
      <PageLayout
      breadcrumbs={[{ label: 'Projects' }]}
      actions={
        projects.length > 0 && (
          <IonButton
            className="header-action-btn"
            color="primary"
            onClick={handleAddProject}
          >
            Add
          </IonButton>
        )
      }
    >
      <PageHeader subtitle="Project provides a workspace for organizing tickets, test cases, and integrations." />

      {/* Projects List */}
      <ProjectsList
        projects={projects}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onCopyApiKey={handleCopyApiKey}
        onAdd={handleAddProject}
      />

      {/* Project Modal — doubles as the create-and-deploy form when
          deployTemplate is set (only meaningful for create, not edit). */}
      <ProjectModal
        isOpen={modalOpen}
        onDismiss={handleModalClose}
        onSubmit={handleModalSubmit}
        initialProject={editingProject}
        isSubmitting={submitting || (!editingProject && deploying)}
        deployTemplate={!editingProject ? deployTemplate : null}
      />

      {/* Project Picker — shown when the user arrived with a deploy
          intent AND already has at least one project. */}
      <ProjectPickerForDeployModal
        isOpen={pickerOpen}
        onDismiss={() => {
          setPickerOpen(false);
          clearDeployIntent();
        }}
        projects={projects}
        templateName={deployTemplate?.name || deployTemplate?.slug}
        templateIcon={deployTemplate?.icon}
        onSelectProject={(pid) => deployToProject(pid)}
        onCreateNewProject={() => {
          setPickerOpen(false);
          setEditingProject(null);
          setModalOpen(true);
        }}
        deploying={deploying}
        deployingProjectId={deployingProjectId}
      />

      {/* Toast Notifications */}
      <IonToast
        isOpen={toast.show}
        message={toast.message}
        duration={3000}
        onDidDismiss={() => setToast({ show: false, message: '', color: '' })}
        cssClass="custom-toast"
      />
    </PageLayout>
    </>
  );
};

export default ProjectsPage;
